// Lib
import React from 'react';
import { Typography, Box } from '@mui/material';

// Components
import FiltersBox from './FiltersBox';
import Search from '../../Search';
import SideMenu from '../../SideMenu';
import Feather from '../../Feather';
import Breadcrumbs from '../../Breadcrumbs';

// Hooks
import useSideMenu from '../../../hooks/useSideMenu';
import useSearch from '../../../hooks/useSearch';
import useUI from '../../../hooks/useUI';
import ViewButtons from './ViewButtons';
import MobileFilters from './MobileFilters';

// Styles
import { useStyles } from './FiltersBarStyles';

// Constants
import { SHIPPING_ADDRESS_BREADCRUMBS } from '../../../constants/cart';

const FiltersBar = ({
  filters,
  isListView,
  onChangeView,
  onAddAddress,
  onChangeSorting,
  importAddresses,
  onApplyMobileFilters,
  searchResults,
  isLoading,
  isAddressBookPage = true,
  onlyContent = false,
}) => {
  const { classes } = useStyles({ onlyContent });
  const { isDesktopMode, isTabletMode, isMobileMode } = useUI();
  const { active, toggleSideMenu, onCloseSideMenu } = useSideMenu();
  const { onChange, searchValue, onClearSearch } = useSearch();

  const renderTitle = () => {
    const getTitle = () =>
      !isAddressBookPage && !onlyContent ? 'Shipping Address' : 'Address Book';

    if (!isAddressBookPage ? isDesktopMode || isTabletMode : isTabletMode) {
      return (
        <Box
          className={classes.titleContainer}
          display="flex"
          justifyContent="space-between"
          alignItems="center">
          {isDesktopMode && !isAddressBookPage ? (
            !onlyContent && <Breadcrumbs items={SHIPPING_ADDRESS_BREADCRUMBS} />
          ) : (
            <Typography className={classes.title} variant="h2">
              {getTitle()}
            </Typography>
          )}
          {isMobileMode ? (
            <Feather
              colorType="primary1"
              type="filters"
              onClick={toggleSideMenu(true)}
            />
          ) : (
            !onlyContent && (
              <Search
                loadingDelay={200}
                value={searchValue}
                onChange={onChange}
                onClearSearch={onClearSearch}
                withMenu
                options={searchResults}
              />
            )
          )}
        </Box>
      );
    }
  };

  const renderControls = () => {
    if (isDesktopMode) {
      return (
        <>
          <Box width="100%" display="flex">
            <ViewButtons
              isListView={isListView}
              onChangeView={onChangeView}
              isMobileMode={isMobileMode}
            />
            <FiltersBox
              isAddressBookPage={isAddressBookPage}
              filters={filters}
              isMobileMode={isMobileMode}
              isDesktopMode={isDesktopMode}
              onAddAddress={onAddAddress}
              onChangeSorting={onChangeSorting}
              importAddresses={importAddresses}
            />
            {onlyContent && (
              <Search
                loadingDelay={200}
                value={searchValue}
                onChange={onChange}
                onClearSearch={onClearSearch}
                withMenu
                options={searchResults}
              />
            )}
            {!isTabletMode && isAddressBookPage && (
              <Search
                loadingDelay={100}
                className={classes.search}
                value={searchValue}
                onChange={onChange}
                onClearSearch={onClearSearch}
                withMenu
                options={searchResults}
              />
            )}
          </Box>
        </>
      );
    }
    return (
      <div className={classes.mobileBox}>
        <ViewButtons
          isListView={isListView}
          onChangeView={onChangeView}
          isMobileMode={isMobileMode}
        />
        {!(isMobileMode && onlyContent) && (
          <FiltersBox
            isAddressBookPage={isAddressBookPage}
            filters={filters}
            isMobileMode={isMobileMode}
            isDesktopMode={isDesktopMode}
            onAddAddress={onAddAddress}
            onChangeSorting={onChangeSorting}
            importAddresses={importAddresses}
          />
        )}
        {!isMobileMode && isTabletMode && onlyContent && (
          <Search
            loadingDelay={200}
            value={searchValue}
            onChange={onChange}
            onClearSearch={onClearSearch}
            withMenu
            options={searchResults}
          />
        )}
      </div>
    );
  };

  if (isLoading) {
    return <></>;
  }

  return (
    <>
      <div className={classes.bar}>
        {renderTitle()}
        {renderControls()}
      </div>
      <SideMenu
        title="Filters"
        styles={{
          width: '100%',
          maxWidth: 'initial',
          '& > div:nth-child(2)': {
            padding: '20px 12px',
          },
        }}
        active={active}
        toggleSideMenu={toggleSideMenu}>
        <MobileFilters
          filters={filters}
          onApplyMobileFilters={onApplyMobileFilters}
          onCloseSideMenu={onCloseSideMenu}
        />
      </SideMenu>
    </>
  );
};

export default FiltersBar;
